import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import Layout from "../../components/Layout";
import PageHeader from "../../components/ui/PageHeader";
import { deleteLevel, fillDefaultLadder, getLevels, Level, saveLevel, uploadLevelImage } from "../../service/LevelService";
import { showSuccessToast } from "../../utils/Toast";
import { Tooltip } from "@mui/material";
import { orangeButton, outlinedButton, StyledTableCell, StyledTableRow, TableWrapper } from "../logs/logStyles";
import LevelPreview from "./LevelPreview";
// A level without its own picture shows nothing here: the backend stores the default pictures with the uploaded
// ones, so a missing picture means the file storage was not there when the ladder was made (press "Opret
// standardstigen" once it is).
const pictureFor = (level: Level) => level.imageUrl || "";

const empty = (): Level => ({ number: 1, heading: "", name: "", minScore: 0, imageUrl: "" });

/**
 * Levels (superadmin): the ladder the participants climb. A level starts at its own number of points and keeps
 * its own picture, so both the steps and the artwork can be changed without a new release of the player app.
 */
export default function Levels() {
  const [t] = useTranslation("global");
  const isMobile = useMediaQuery("(max-width:600px)");
  const [levels, setLevels] = useState<Level[]>([]);
  const [editing, setEditing] = useState<Level | null>(null);
  const [busy, setBusy] = useState(false);

  const load = () => getLevels().then(setLevels);
  useEffect(() => {
    load();
  }, []);

  const startNew = () => {
    const next = levels.length ? Math.max(...levels.map((level) => level.number)) + 1 : 1;
    const last = levels[levels.length - 1];
    setEditing({ ...empty(), number: next, heading: last?.heading || "", name: "", minScore: (last?.minScore || 0) + 10000 });
  };

  const save = async () => {
    if (!editing) return;
    setBusy(true);
    const saved = await saveLevel(editing);
    setBusy(false);
    if (saved) {
      showSuccessToast(t("levels.saved", { number: saved.number }), isMobile);
      setEditing(null);
      load();
    }
  };

  const remove = async (level: Level) => {
    if (!level.id || !window.confirm(t("levels.confirmDelete", { number: level.number }))) return;
    if (await deleteLevel(level.id)) load();
  };

  const fillLadder = async () => {
    const result = await fillDefaultLadder();
    if (result) {
      showSuccessToast(t("levels.ladderFilled", { added: result.added, renamed: result.renamed ?? 0, pictures: result.pictures ?? 0 }), isMobile);
      load();
    }
  };

  const pickImage = async (file?: File | null) => {
    if (!file || !editing) return;
    setBusy(true);
    const url = await uploadLevelImage(file);
    setBusy(false);
    if (url) setEditing({ ...editing, imageUrl: url });
  };

  // How far apart the steps are - the number that decides how many breefs it takes to move up
  const gapBefore = (index: number) => (index === 0 ? 0 : levels[index].minScore - levels[index - 1].minScore);

  return (
    <Layout>
      <PageHeader title={t("levels.title")} description={t("levels.intro")} />

      <Box sx={{ width: "100%", mb: 2, display: "flex", gap: 1, flexWrap: "wrap", justifyContent: "flex-end" }}>
        <Button variant="outlined" sx={outlinedButton} onClick={fillLadder}>
          {t("levels.fillLadder")}
        </Button>
        <Button variant="contained" sx={orangeButton} onClick={startNew}>
          {t("levels.add")}
        </Button>
      </Box>

      <TableWrapper component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <StyledTableCell>{t("levels.number")}</StyledTableCell>
              <StyledTableCell>{t("levels.name")}</StyledTableCell>
              <StyledTableCell>{t("levels.heading")}</StyledTableCell>
              <StyledTableCell>{t("levels.minScore")}</StyledTableCell>
              <StyledTableCell>{t("levels.gap")}</StyledTableCell>
              <StyledTableCell>{t("levels.image")}</StyledTableCell>
              <StyledTableCell align="right">{t("levels.actions")}</StyledTableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {levels.map((level, index) => (
              <StyledTableRow key={level.id || level.number}>
                <TableCell>{level.number}</TableCell>
                <TableCell>{level.name || "-"}</TableCell>
                <TableCell>{level.heading || "-"}</TableCell>
                <TableCell>{level.minScore.toLocaleString("da-DK")}</TableCell>
                <TableCell>{index === 0 ? "-" : gapBefore(index).toLocaleString("da-DK")}</TableCell>
                <TableCell>
                  {/* Hold the miniature to see the card as the participant's phone shows it */}
                  <Tooltip
                    placement="right"
                    title={<LevelPreview level={level} of={levels.length} height={520} />}
                    componentsProps={{ tooltip: { sx: { bgcolor: "transparent", p: 0, boxShadow: 6, borderRadius: 6 } } }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      {level.imageUrl && <img src={pictureFor(level)} alt="" style={{ height: 44, borderRadius: 6, objectFit: "cover", border: "1px solid rgba(0,0,0,0.08)" }} />}
                      {!level.imageUrl && (
                        <Typography variant="body2" color="text.secondary">
                          {t("levels.noImage")}
                        </Typography>
                      )}
                    </Box>
                  </Tooltip>
                </TableCell>
                <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
                  <Button size="small" variant="outlined" sx={outlinedButton} onClick={() => setEditing(level)}>
                    {t("levels.edit")}
                  </Button>{" "}
                  <Button size="small" variant="outlined" sx={outlinedButton} onClick={() => remove(level)}>
                    {t("levels.delete")}
                  </Button>
                </TableCell>
              </StyledTableRow>
            ))}
            {!levels.length && (
              <TableRow>
                <TableCell colSpan={7}>{t("levels.none")}</TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableWrapper>

      <Dialog open={Boolean(editing)} onClose={() => setEditing(null)} fullWidth maxWidth="md">
        <DialogTitle>{editing?.id ? t("levels.editTitle", { number: editing?.number }) : t("levels.addTitle")}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: "flex", gap: 3, flexDirection: isMobile ? "column" : "row", alignItems: "flex-start" }}>
          <Box sx={{ flex: 1, minWidth: 0, width: "100%" }}>
          <TextField
            margin="dense"
            fullWidth
            type="number"
            label={t("levels.number")}
            value={editing?.number ?? ""}
            onChange={(event) => editing && setEditing({ ...editing, number: Number(event.target.value) })}
          />
          <TextField
            margin="dense"
            fullWidth
            label={t("levels.name")}
            value={editing?.name ?? ""}
            onChange={(event) => editing && setEditing({ ...editing, name: event.target.value })}
          />
          <TextField
            margin="dense"
            fullWidth
            label={t("levels.heading")}
            helperText={t("levels.headingHelp")}
            value={editing?.heading ?? ""}
            onChange={(event) => editing && setEditing({ ...editing, heading: event.target.value })}
          />
          <TextField
            margin="dense"
            fullWidth
            type="number"
            label={t("levels.minScore")}
            helperText={t("levels.minScoreHelp")}
            value={editing?.minScore ?? ""}
            onChange={(event) => editing && setEditing({ ...editing, minScore: Number(event.target.value) })}
          />
          <Box sx={{ mt: 2, display: "flex", alignItems: "center", gap: 2 }}>
            {editing?.imageUrl && <img src={pictureFor(editing)} alt="" style={{ height: 120, borderRadius: 8, objectFit: "cover", border: "1px solid rgba(0,0,0,0.08)" }} />}
            <Button variant="outlined" component="label" sx={outlinedButton} disabled={busy}>
              {t("levels.upload")}
              <input hidden type="file" accept="image/png,image/jpeg,image/webp" onChange={(event) => pickImage(event.target.files?.[0])} />
            </Button>
            <Typography variant="body2" color="text.secondary">
              {t("levels.uploadHelp")}
            </Typography>
          </Box>
          </Box>
          {/* The card as the phone shows it, with what is typed right now */}
          {editing && (
            <Box sx={{ pt: 1, alignSelf: "center" }}>
              <LevelPreview level={editing} of={Math.max(levels.length, editing.number)} height={560} />
              <Typography variant="caption" color="text.secondary" sx={{ display: "block", textAlign: "center", mt: 1 }}>
                {t("levels.preview")}
              </Typography>
            </Box>
          )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditing(null)} sx={outlinedButton} variant="outlined">
            {t("levels.cancel")}
          </Button>
          <Button onClick={save} sx={orangeButton} variant="contained" disabled={busy}>
            {t("levels.save")}
          </Button>
        </DialogActions>
      </Dialog>
    </Layout>
  );
}
